import React, { useState, useEffect } from 'react';
import { Trophy, ChevronRight } from 'lucide-react';
import { supabase } from '../lib/supabase.ts';
import { UserAvatar } from './UserAvatar.tsx';
import { soundEngine } from '../utils/soundAndSpeech.ts';
import {
  formatDisplayNumber,
  compareLeaderboardEntries,
} from '../config/scoringConfig.ts';

interface Top10PlayersCardProps {
  initialLeaderboard?: any[];
  onOpenUserProfile?: (uidOrUsername: string) => void;
  onViewFullRanks: () => void;
}

export const Top10PlayersCard: React.FC<Top10PlayersCardProps> = ({
  initialLeaderboard = [],
  onOpenUserProfile,
  onViewFullRanks,
}) => {
  const [entries, setEntries] = useState<any[]>(initialLeaderboard);
  const [isLoading, setIsLoading] = useState<boolean>(initialLeaderboard.length === 0);

  const fetchTop10 = async () => {
    try {
      const { data, error } = await supabase
        .from('leaderboards')
        .select('*')
        .order('total_points', { ascending: false })
        .order('accuracy', { ascending: false })
        .limit(10);

      if (!error && data) {
        setEntries(data);
      } else {
        const res = await fetch('/api/bootstrap');
        if (res.ok) {
          const json = await res.json();
          if (Array.isArray(json.leaderboards)) {
            setEntries(json.leaderboards.slice(0, 10));
          }
        }
      }
    } catch (err) {
      console.warn('[Top10PlayersCard] Failed to fetch top 10:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (initialLeaderboard.length > 0) {
      setEntries(initialLeaderboard.slice(0, 10));
      setIsLoading(false);
    } else {
      fetchTop10();
    }

    const channel = supabase
      .channel('home-top10-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'leaderboards',
        },
        () => {
          fetchTop10();
        }
      )
      .subscribe();

    const intervalId = setInterval(() => {
      fetchTop10();
    }, 30000);

    return () => {
      try {
        supabase.removeChannel(channel);
      } catch {
        // ignore
      }
      clearInterval(intervalId);
    };
  }, [initialLeaderboard]);

  const top10 = [...entries]
    .sort((a, b) =>
      compareLeaderboardEntries(
        {
          xp: Number(a.totalPoints ?? a.total_points ?? 0),
          accuracy: Number(a.accuracy ?? 0),
          updatedAt: a.updatedAt ?? a.updated_at,
        },
        {
          xp: Number(b.totalPoints ?? b.total_points ?? 0),
          accuracy: Number(b.accuracy ?? 0),
          updatedAt: b.updatedAt ?? b.updated_at,
        }
      )
    )
    .slice(0, 10);

  return (
    <div className="rounded-2xl bg-[#161618] border border-[#27272A] p-4 sm:p-6 shadow-sm space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0">
            <Trophy className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base sm:text-lg font-bold text-white tracking-tight">
              Top 10 Global Scholars
            </h2>
            <p className="text-xs text-[#A3A3A3]">
              Global ranks 1–10 by total Scripture XP.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            soundEngine.playClick();
            onViewFullRanks();
          }}
          className="inline-flex items-center gap-1 text-xs font-semibold text-[#F97316] hover:text-[#FB923C] transition-colors cursor-pointer shrink-0"
        >
          <span>View full Ranks</span>
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {isLoading && top10.length === 0 ? (
        <div className="space-y-3 py-3">
          {[1, 2, 3, 4, 5].map((idx) => (
            <div key={idx} className="flex items-center justify-between p-3 rounded-xl bg-[#141414] border border-[#262626] animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-[#262626]" />
                <div className="w-8 h-8 rounded-full bg-[#262626]" />
                <div className="space-y-1.5">
                  <div className="w-28 h-3 rounded bg-[#262626]" />
                  <div className="w-16 h-2.5 rounded bg-[#262626]" />
                </div>
              </div>
              <div className="w-16 h-4 rounded bg-[#262626]" />
            </div>
          ))}
        </div>
      ) : top10.length === 0 ? (
        <div className="py-8 text-center text-xs text-[#737373]">
          No global ranking entries found yet. Be the first to play!
        </div>
      ) : (
        <div className="divide-y divide-[#262626] overflow-hidden rounded-xl border border-[#262626] bg-[#141414]">
          {/* Header Row: Show "XP" once in the column header, not in each row */}
          <div className="flex items-center justify-between px-3.5 py-2.5 bg-[#18181A] text-[11px] font-semibold uppercase tracking-wider text-[#A3A3A3]">
            <div className="flex items-center gap-3">
              <span className="w-7 sm:w-8 text-center">Rank</span>
              <span>Scholar</span>
            </div>
            <span className="pr-5">XP</span>
          </div>

          {top10.map((entry, index) => {
            const rank = index + 1;
            const isGold = rank === 1;
            const isSilver = rank === 2;
            const isBronze = rank === 3;
            const xpVal = Number(entry.totalPoints ?? entry.total_points ?? 0);
            const fmtXp = formatDisplayNumber(xpVal);
            const accVal = entry.accuracy;

            return (
              <div
                key={entry.userUid || entry.id || index}
                role="button"
                tabIndex={0}
                onClick={() => {
                  soundEngine.playClick();
                  if (onOpenUserProfile) {
                    onOpenUserProfile(entry.userUid);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    soundEngine.playClick();
                    if (onOpenUserProfile) {
                      onOpenUserProfile(entry.userUid);
                    }
                  }
                }}
                className="flex items-center justify-between p-3 sm:p-3.5 hover:bg-[#1C1C1C] transition-colors cursor-pointer select-none group"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-7 sm:w-8 flex items-center justify-center shrink-0">
                    {isGold ? (
                      <div className="w-7 h-7 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400 font-bold text-xs shadow-sm">
                        1
                      </div>
                    ) : isSilver ? (
                      <div className="w-7 h-7 rounded-full bg-slate-300/20 border border-slate-300/40 flex items-center justify-center text-slate-200 font-bold text-xs shadow-sm">
                        2
                      </div>
                    ) : isBronze ? (
                      <div className="w-7 h-7 rounded-full bg-amber-700/20 border-amber-600/40 border flex items-center justify-center text-amber-500 font-bold text-xs shadow-sm">
                        3
                      </div>
                    ) : (
                      <span className="text-xs font-mono font-bold text-[#A3A3A3]">
                        {rank}
                      </span>
                    )}
                  </div>

                  <UserAvatar
                    src={entry.avatarUrl || entry.avatar_url}
                    name={entry.displayName || entry.display_name || entry.username}
                    sizeClassName="w-8 h-8 sm:w-9 sm:h-9"
                    textClassName="text-[10px] font-bold text-amber-400"
                  />

                  <div className="min-w-0 flex-1 pr-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs sm:text-sm font-bold text-white truncate group-hover:text-[#F97316] transition-colors">
                        {entry.displayName || entry.display_name || entry.username || 'Scholar'}
                      </span>
                      <span className="hidden sm:inline-block px-2 py-0.5 rounded-full bg-[#262626] border border-[#404040] text-[10px] font-medium text-[#D4D4D4] shrink-0">
                        {entry.titleBadge || entry.title_badge || 'Scripture Seeker'}
                      </span>
                    </div>
                    <div className="text-[11px] text-[#A3A3A3] sm:hidden truncate">
                      {entry.titleBadge || entry.title_badge || 'Scripture Seeker'}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0 pl-2">
                  <div className="text-right">
                    <div
                      className={`text-xs sm:text-sm font-extrabold font-mono tabular-nums ${
                        fmtXp.isZero ? 'text-[#737373]' : 'text-amber-400'
                      }`}
                    >
                      {fmtXp.text}
                    </div>
                    <div className="text-[11px] text-[#737373] font-mono">
                      {accVal !== undefined && accVal !== null ? `${accVal}%` : ''}
                    </div>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-[#737373] group-hover:text-white transition-colors shrink-0" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
